import { useState, useEffect, useRef, useCallback } from 'react';

const useAnimation = () => {
  const [multiplierFlash, setMultiplierFlash] = useState(false);
  const [isMarkerBouncing, setIsMarkerBouncing] = useState(false);
  const [bounceDirection, setBounceDirection] = useState('left');
  const [redSectionFlashing, setRedSectionFlashing] = useState(false);
  const [lastBounceTime, setLastBounceTime] = useState(0);
  const [bounceProgress, setBounceProgress] = useState(0);
  
  const bounceAnimationRef = useRef(null);
  const isMarkerBouncingRef = useRef(false);

  useEffect(() => {
    isMarkerBouncingRef.current = isMarkerBouncing;
  }, [isMarkerBouncing]);

  const flashMultiplierDisplay = useCallback(() => {
    setMultiplierFlash(true);
    setTimeout(() => {
      setMultiplierFlash(false);
    }, 500);
  }, []);

  const triggerRedSectionFlash = useCallback(() => {
    const flashSequence = [
      { flash: true, delay: 0 },
      { flash: false, delay: 150 },
      { flash: true, delay: 10 },
      { flash: false, delay: 150 },
      { flash: true, delay: 5 },
      { flash: false, delay: 150 }
    ];
    
    let totalDelay = 0;
    flashSequence.forEach(item => {
      setTimeout(() => {
        setRedSectionFlashing(item.flash);
      }, totalDelay);
      totalDelay += item.delay;
    });
  }, []);

  const startBounceAnimation = useCallback((expectedScanTime, playSound, onComplete) => {
    setBounceProgress(0);
    setIsMarkerBouncing(true);
    isMarkerBouncingRef.current = true;

    playSound('timerOut');

    let animationProgress = 0;
    let lastTimestamp = performance.now();

    const animateBounce = timestamp => {
      const elapsed = timestamp - lastTimestamp;
      lastTimestamp = timestamp;

      animationProgress += elapsed / 600;

      if (animationProgress >= 1) {
        setIsMarkerBouncing(false);
        isMarkerBouncingRef.current = false;

        if (onComplete) {
          onComplete(expectedScanTime * 0.75);
        }
        return;
      }

      setBounceProgress(animationProgress);
      bounceAnimationRef.current = requestAnimationFrame(animateBounce);
    };

    if (bounceAnimationRef.current) {
      cancelAnimationFrame(bounceAnimationRef.current);
    }
    bounceAnimationRef.current = requestAnimationFrame(animateBounce);
  }, []);

  const calculateBouncePosition = useCallback(progress => {
    const rightEdge = 1.0;
    const safeRedPoint = 0.85;

    if (progress < 0.5) {
      const normalizedT = progress / 0.5;
      const ease = 1 - Math.pow(1 - normalizedT, 3);
      return rightEdge - ease * 0.15;
    } else {
      const normalizedT = (progress - 0.5) / 0.5;
      return safeRedPoint + normalizedT * 0.15;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (bounceAnimationRef.current) {
        cancelAnimationFrame(bounceAnimationRef.current);
        bounceAnimationRef.current = null;
      }
    };
  }, []);

  return {
    multiplierFlash,
    isMarkerBouncing,
    bounceDirection,
    redSectionFlashing,
    lastBounceTime,
    bounceProgress,
    isMarkerBouncingRef,
    flashMultiplierDisplay,
    triggerRedSectionFlash,
    startBounceAnimation,
    calculateBouncePosition,
    setLastBounceTime
  };
};

export default useAnimation;
